import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react';
import { API_ERROR_TYPES, isApiError, type ApiError } from '../api/fetch/apiClient';
import { setGlobalErrorHandler, clearGlobalErrorHandler } from '../utils/queryClient';

export interface ApiErrorState {
  type: string;
  message: string;
  status?: number;
  timestamp: number;
}

export interface ApiErrorContextValue {
  apiError: ApiErrorState | null;
  hasApiError: boolean;
  reportError: (error: unknown) => void;
  clearError: () => void;
}

const ApiErrorContext = createContext<ApiErrorContextValue | null>(null);

interface ApiErrorProviderProps {
  children: ReactNode;
}

function ApiErrorProvider({ children }: ApiErrorProviderProps) {
  const [apiError, setApiError] = useState<ApiErrorState | null>(null);

  const reportError = useCallback((error: unknown) => {
    if (!isApiError(error)) return;

    if (error.type === API_ERROR_TYPES.NETWORK) {
      setApiError({
        type: error.type,
        message: error.message,
        status: error.status,
        timestamp: Date.now(),
      });
    }
  }, []);

  useEffect(() => {
    setGlobalErrorHandler(reportError as (error: ApiError) => void);
    return () => {
      clearGlobalErrorHandler();
    };
  }, [reportError]);

  const clearError = useCallback(() => {
    setApiError(null);
  }, []);

  const hasApiError = apiError !== null;

  const value: ApiErrorContextValue = {
    apiError,
    hasApiError,
    reportError,
    clearError,
  };

  return <ApiErrorContext.Provider value={value}>{children}</ApiErrorContext.Provider>;
}

function useApiError(): ApiErrorContextValue {
  const context = useContext(ApiErrorContext);
  if (!context) {
    throw new Error('useApiError must be used within an ApiErrorProvider');
  }
  return context;
}

export { ApiErrorContext, ApiErrorProvider, useApiError };
